import React from 'react';
import { MdOutlineAddCircleOutline, MdOutlineRemoveCircleOutline } from 'react-icons/md';
import { FiEdit } from 'react-icons/fi';
import { instanceToPlain } from 'class-transformer';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { selectPortfolioCompositeSelected } from '../portfolio-allocation/collapsible-composite-slice';
import { selectPositionSelected, setSelectedPosition } from '../portfolio-allocation/collapsible-position-slice';
import { removeComposite, removePosition } from './command-bar-slice';
import { useAuth0 } from '@auth0/auth0-react';
import { selectIsAnyPositionEditing, setPositionEditing } from './edit-position-slice';
import Position from '../../../../api/models/portfolioPosition';
import EditPosition from './edit-position';
import { selectIsAnyCompositeEditing, setCompositeEditing } from './edit-composite-slice';
import Category from '../../../../api/models/category';
import PortfolioComposite from '../../../../api/models/portfolioComposite';
import EditComposite from './edit-composite';
import { recalculatePortfolio } from '../../portfolios-slice';

/**
 * A command bar for CRUD operations on positions and categories.
 */
const CommandBar: React.FC = () => {

  const { getAccessTokenSilently } = useAuth0();

  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const compositeSelected = useAppSelector(selectPortfolioCompositeSelected);
  const positionSelected = useAppSelector(selectPositionSelected);
  const isAnyCompositeEditing = useAppSelector(selectIsAnyCompositeEditing);
  const isAnyPositionEditing = useAppSelector(selectIsAnyPositionEditing);

  const isAnyCompositeSelected = compositeSelected ? true : false;
  const isAnyPositionSelected = positionSelected ? true : false;
  const isRootCategory = (compositeSelected?.parentCompositeId === undefined || compositeSelected?.parentCompositeId === null);

  const createPositionHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (compositeSelected) {
      const positionCreating = new Position(undefined, compositeSelected.portfolioId, undefined, '', 1, 1000, new Date(), new Date(), 10, compositeSelected.id);
      dispatch(setPositionEditing(({ position: instanceToPlain(positionCreating), parent: instanceToPlain(compositeSelected) })));
    }
  }

  const editPositionHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (positionSelected) {
      dispatch(setPositionEditing(({ position: instanceToPlain(positionSelected), parent: undefined })));
    }
  }

  const removePositionHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (positionSelected !== undefined) {
      const execute = async () => {
        const accessToken = await getAccessTokenSilently();
        dispatch(removePosition({ accessToken, position: positionSelected }));
        dispatch(setPositionEditing({ position: undefined, parent: undefined }));
        dispatch(setSelectedPosition(undefined));
        dispatch(recalculatePortfolio({ portfolioId: positionSelected.portfolioId }));
      }
      execute();
    }
  }

  const createUnderlyingCompositeHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    const execute = async () => {
      if (compositeSelected) {
        const category = new Category(undefined, '', '', '');
        const compositeCreating = new PortfolioComposite(undefined, compositeSelected.portfolioId, category, [], 10, compositeSelected.id);
        dispatch(setCompositeEditing(({ composite: instanceToPlain(compositeCreating), parent: instanceToPlain(compositeSelected) })));
      }
    }
    execute();
  }

  const updateCompositeHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (compositeSelected) {
      dispatch(setCompositeEditing({ composite: instanceToPlain(compositeSelected), parent: undefined }));
    }
  }

  const removeCompositeHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (compositeSelected !== undefined) {
      const execute = async () => {
        const accessToken = await getAccessTokenSilently();
        dispatch(removeComposite({ accessToken, composite: compositeSelected }))
        dispatch(recalculatePortfolio({ portfolioId: compositeSelected.portfolioId }));
      }
      execute();
    }
  }

  return (
    <div className={`lg:relative flex flex-col max-lg:w-full lg:h-full lg:w-96 lg:mt-0 border-t p-2 lg:border-t-0 border-slate-500 bg-marble bg-cover dark:bg-neutral-900 dark:bg-blend-darken text-white dark:text-neutral-300 text-xs lg:text-base font-semibold items-center justify-center lg:justify-start z-50`}>
      <div className={`${isAnyPositionEditing ? 'absolute' : 'hidden'} w-full h-full overflow-y-auto text-slate-700 bg-marble max-lg:bottom-0 lg:top-0`}>
        {<EditPosition />}
      </div>
      <div className={`${isAnyCompositeEditing ? 'absolute' : 'hidden'} w-full h-full overflow-y-auto text-slate-700 bg-marble max-lg:bottom-0 lg:top-0`}>
        {<EditComposite />}
      </div>
      <div className={`${(isAnyCompositeSelected || isAnyPositionSelected) ? 'max-lg:hidden' : 'max-lg:flex'} lg:flex lg:mt-4 p-4 font-semibold text-slate-700 text-center justify-center`}>
        {!isAnyCompositeSelected && !isAnyPositionSelected && <label>Selecione uma categoria ou posição para alterar o portfolio</label>}
        {isAnyCompositeSelected && <label>Categoria "{compositeSelected?.category.name}"</label>}
        {isAnyPositionSelected && <label>Posição "{positionSelected?.security?.symbol ?? positionSelected?.description}"</label>}
      </div>
      <div className='w-full flex flex-row lg:flex-col flex-wrap justify-center'>
        <div onMouseDown={createPositionHandler} className={`${isAnyCompositeSelected && !isRootCategory ? 'flex' : 'hidden'} grow max-w-28 lg:max-w-full cursor-pointer m-1 lg:m-2 items-center justify-center px-2 lg:px-4 py-1 lg:py-2 rounded-full bg-blue-950 shadow-md shadow-neutral-400/60 dark:shadow-slate-900/80 hover:opacity-85`}>
          <MdOutlineAddCircleOutline size={20} />
          <div className='ml-1 text-center justify-center'>Posição</div>
        </div>
        <div onMouseDown={editPositionHandler} className={`${isAnyPositionSelected ? 'flex' : 'hidden'} grow max-w-28 lg:max-w-full cursor-pointer m-1 lg:m-2 items-center justify-center px-2 lg:px-4 py-1 lg:py-2 rounded-full bg-blue-950 shadow-md shadow-neutral-400/60 dark:shadow-slate-900/80 hover:opacity-85`}>
          <FiEdit size={20} />
          <div className='ml-1 text-center justify-center'>Posição</div>
        </div>
        <div onMouseDown={removePositionHandler} className={`${isAnyPositionSelected ? 'flex' : 'hidden'} grow max-w-28 lg:max-w-full cursor-pointer m-1 lg:m-2 items-center justify-center px-2 lg:px-4 py-1 lg:py-2 rounded-full bg-red-700 shadow-md shadow-neutral-400/60 dark:shadow-slate-900/80 hover:opacity-85`}>
          <MdOutlineRemoveCircleOutline size={20} />
          <div className='ml-1 text-center justify-center'>Posição</div>
        </div>
      </div>
      <div className='w-full flex flex-row lg:flex-col flex-wrap justify-center'>
        <div onMouseDown={createUnderlyingCompositeHandler} className={`${isAnyCompositeSelected ? 'flex' : 'hidden'} grow max-w-28 lg:max-w-full cursor-pointer m-1 lg:m-2 items-center justify-center px-2 lg:px-4 py-1 lg:py-2 rounded-full bg-blue-950 shadow-md shadow-neutral-400/60 dark:shadow-slate-900/80 hover:opacity-85`}>
          <MdOutlineAddCircleOutline size={20} />
          <div className='ml-1 text-center justify-center'>Subcategoria</div>
        </div>
        <div onMouseDown={updateCompositeHandler} className={`${isAnyCompositeSelected && !isRootCategory ? 'flex' : 'hidden'} grow max-w-28 lg:max-w-full cursor-pointer m-1 lg:m-2 items-center justify-center px-2 lg:px-4 py-1 lg:py-2 rounded-full bg-blue-950 shadow-md shadow-neutral-400/60 dark:shadow-slate-900/80 hover:opacity-85`}>
          <FiEdit size={20} />
          <div className='ml-1 text-center justify-center'>Categoria</div>
        </div>
        <div onMouseDown={removeCompositeHandler} className={`${isAnyCompositeSelected && !isRootCategory ? 'flex' : 'hidden'} grow max-w-28 lg:max-w-full cursor-pointer m-1 lg:m-2 items-center justify-center px-2 lg:px-4 py-1 lg:py-2 rounded-full bg-red-700/95 shadow-md shadow-neutral-400/60 dark:shadow-slate-900/80 hover:opacity-85`}>
          <MdOutlineRemoveCircleOutline size={20} />
          <div className='ml-1 text-center justify-center'>Categoria</div>
        </div>
      </div>
    </div >
  )
}

export default CommandBar;